import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Tabs as MUITabs, Box, Stack, Tab } from "@mui/material";
import styles from "./styles";
import { colors, radius } from "../../utils";
import tabsVariantFor from "./tabsVariant";

// fe-testids-segment-color-custom-tab: every tab this shared component renders
// (Preset/Custom/Wheel Palette in SegmentColor, fill-type tabs, gallery
// Customize tabs, etc.) needs a stable QA hook — text= locators are ambiguous
// wherever a tab label repeats elsewhere on the same screen.
const slugify = (str) =>
  String(str)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// gordon-0915-block-layout-tab-disabled-state-explainer: a category listed in
// `disabledCategories` (e.g. every block's "Layout" tab before it has
// content — VideoSettings/SocialLinksSettings/LinkWheelSettings) used to
// render pixel-identical to a normal inactive tab and swallow clicks
// silently. Shared with the CustomTabs sibling component's fix.
export const DISABLED_TAB_EXPLAINER =
  "Add content first - choose or upload media to unlock Layout";

const Tabs = ({
  categories,
  selectedCategory,
  onCategoryChange,
  color = "#81459B",
  indicatorColor = "#C283DA",
  tabStyle,
  disabledCategories = [],
  disabledHints = {},
  categoriesWithIcons = [],
  variant,
  suffixStyle = {},
  sx = {},
  scrollButtons,
  allowScrollButtonsMobile,
  ariaLabel,
  testId,
}) => {
  const classes = styles(color, indicatorColor);

  // Which disabled category's explainer is currently shown, if any.
  const [explainerValue, setExplainerValue] = useState(null);

  // If the open category's disabled flag flips false (content was just
  // added), drop the stale explainer instead of leaving it stuck open under
  // a tab that now works normally.
  useEffect(() => {
    if (explainerValue !== null && !disabledCategories.includes(explainerValue)) {
      setExplainerValue(null);
    }
  }, [disabledCategories, explainerValue]);

  const disabledSx = (disabled) =>
    disabled ? { opacity: 0.5, cursor: "not-allowed" } : {};

  // T22.260 (Kelly 2026-10-14, Gallery > Collaborations): hovering the tab
  // that is ALREADY selected painted the full-width #EFE9FE hover band under
  // a pointer cursor — on the 2-tab Collaborations strip that is a 516px
  // purple bar ("Shared With Me") that reads as a link, yet clicking it does
  // nothing because it is already the active tab. The selected tab is
  // already marked by its colour + indicator, so it gets no hover band and a
  // plain cursor; inactive tabs keep the hover affordance (they do switch).
  const hoverSx = (disabled, selected) =>
    disabled || selected ? undefined : { backgroundColor: "#EFE9FE !important" };
  const selectedSx = (selected) => (selected ? { cursor: "default" } : {});

  // A disabled tab must never activate its panel — the native MUI `disabled`
  // prop would achieve that, but it also blocks the click event entirely
  // (browsers/RTL don't fire click on disabled buttons), which would make it
  // impossible to show the explainer. So switching is blocked here instead,
  // leaving the Tab itself clickable.
  const handleCategoryChange = (event, newValue) => {
    if (disabledCategories.includes(newValue)) {
      return;
    }
    setExplainerValue(null);
    onCategoryChange(event, newValue);
  };

  const onTabClick = (value, disabled) => () => {
    if (!disabled) {
      return;
    }
    setExplainerValue((prev) => (prev === value ? null : value));
  };

  // fix-hw0917-o3-wallpaper-tabstrip-still-clips-svg-patterns-and-hides-selected-tab:
  // MEASURED live on web-dev.iyuga.net (Segment Settings > Wallpaper strip,
  // bundle main.5bfcc2cc.js) — MUI's scrollable Tabs only auto-scrolls a tab
  // into view on SELECTION (activation), via its own scrollSelectedIntoView.
  // Arrow-key navigation moves keyboard FOCUS between tabs without selecting
  // them (standard ARIA tablist "manual activation" pattern), and MUI never
  // scrolls the merely-focused tab into view — a keyboard user arrowing past
  // "Colors"/"Gradient"/"Image" toward "SVG Patterns" keeps moving focus onto
  // tabs the 250px scroller viewport never reveals, with no way to see which
  // tab is about to be activated. Scrolling the focused tab into view on
  // every focus event (mouse click already re-fires focus, so this is a
  // superset, not a special case) closes that gap.
  const onTabFocus = (event) => {
    event.currentTarget.scrollIntoView({ block: "nearest", inline: "nearest" });
  };

  // fix-segment-environment-1024-text-wrap: consumers that never pass `variant`
  // (e.g. Wallpaper/children/Image) used to fall back to a hardcoded "fullWidth",
  // which divides a narrow side-pane track into N equal slices no matter how many
  // tabs it holds -- the failure mode this bug is about. Resolve automatically from
  // the same tabsVariantFor threshold MediaSelection already opts into explicitly,
  // so every pane inherits scrollable-when-crowded for free. An explicit `variant`
  // prop still wins (existing call sites that already tuned their own threshold,
  // e.g. WALLPAPER_TAB_PROPS, are unaffected).
  const resolvedVariant =
    variant ??
    tabsVariantFor(
      categoriesWithIcons.length > 0 ? categoriesWithIcons : categories
    );

  const renderTabs = () => {
    if (categoriesWithIcons.length > 0) {
      return categoriesWithIcons.map(
        ({ label, value, icon, suffix, customIcon }) => {
          const disabled = disabledCategories.includes(value);
          const selected = value === selectedCategory;
          return (
          <Tab
            key={value}
            data-testid={`tab-${slugify(value)}`}
            aria-disabled={disabled}
            onClick={onTabClick(value, disabled)}
            onFocus={onTabFocus}
            label={
              <Stack
                alignItems={"center"}
                gap={1}
                direction={"row"}
                minWidth={0}
              >
                {/* <img style={{color:'red',fill:'red',stroke:'red'}} width={20} height={20} alt={icon} src={icon}></img> */}
                {!!icon &&
                  React.cloneElement(icon, {
                    width: 20,
                    height: 20,
                    fill:
                      selectedCategory === value
                        ? colors.PrimaryPurple
                        : colors.lightGrey,
                  })}
                <div
                  data-testid={`tab-label-${slugify(value)}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "DM Sans",
                    fontSize: 14,
                    fontWeight: 400,
                    color:
                      selectedCategory === value
                        ? colors.PrimaryPurple
                        : colors.lightGrey,
                    // fix-editwheel-seglib-segment-sets-tab-clipped-v1: only
                    // takes effect once an ancestor Tab is squeezed below this
                    // label's natural width (minWidth:0 tabStyle) — inert
                    // (nothing to clip) for every other Tabs consumer today.
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </div>
                {!!suffix && (
                  <div
                    style={{
                      borderRadius: 50,
                      padding: "2px 10px",
                      backgroundColor:
                        selectedCategory === value ? "#EFE9FE" : "#ECECEC",
                      ...suffixStyle,
                    }}
                  >
                    <div
                      style={{
                        alignItems: "center",
                        fontFamily: "DM Sans",
                        fontSize: 12,
                        color:
                          selectedCategory === value
                            ? colors.PrimaryPurple
                            : "inherit",
                      }}
                    >
                      {suffix}
                    </div>
                  </div>
                )}

                {!!customIcon && customIcon}
              </Stack>
            }
            value={value}
            sx={{
              ...classes.tabLabel,
              // Corner-round the purple hover/selection highlight (radius.md 8px)
              // so it is never a square band flush to the tab edges — consistent
              // with the side-pane pills (Accordion/ToolAccordion/CustomTabs).
              borderRadius: `${radius.md}px`,
              transition: "background-color 0.2s ease",
              "&:hover": hoverSx(disabled, selected),
            }}
            style={{
              ...tabStyle,
              ...selectedSx(selected),
              ...disabledSx(disabled),
              transition: "background-color 0.2s ease",
              "&:hover": hoverSx(disabled, selected),
            }}
          />
          );
        }
      );
    }

    return categories.map((category) => {
      const disabled = disabledCategories.includes(category);
      const selected = category === selectedCategory;
      return (
        <Tab
          key={category}
          data-testid={`tab-${slugify(category)}`}
          label={category}
          value={category}
          aria-disabled={disabled}
          onClick={onTabClick(category, disabled)}
          onFocus={onTabFocus}
          sx={{
            ...classes.tabLabel,
            // Corner-round the purple hover/selection highlight (radius.md 8px)
            // so it is never a square band flush to the tab edges — consistent
            // with the side-pane pills (Accordion/ToolAccordion/CustomTabs).
            borderRadius: `${radius.md}px`,
            transition: "background-color 0.2s ease",
            "&:hover": hoverSx(disabled, selected),
          }}
          style={{
            ...tabStyle,
            ...selectedSx(selected),
            ...disabledSx(disabled),
            transition: "background-color 0.2s ease",
            "&:hover": hoverSx(disabled, selected),
          }}
        />
      );
    });
  };

  return (
    <>
      <MUITabs
        value={selectedCategory}
        onChange={handleCategoryChange}
        aria-label={ariaLabel || "Content tabs"}
        variant={resolvedVariant}
        data-testid={testId}
        data-variant={resolvedVariant}
        scrollButtons={scrollButtons}
        allowScrollButtonsMobile={allowScrollButtonsMobile}
        TabIndicatorProps={{
          style: classes.tabContainer,
        }}
        sx={{
          minHeight: "auto",
          "& .MuiTab-root": {
            minWidth: "48px",
            padding: "8px 12px",
            fontSize: "13px",
            fontWeight: 500,
            whiteSpace: "nowrap",
            overflowWrap: "normal",
          },
          "& .MuiTabs-flexContainer": {
            gap: "4px",
          },
          ...sx,
        }}
      >
        {renderTabs()}
      </MUITabs>
      {/* Inline explainer for whichever disabled category was just
          clicked/tapped — role="status" + aria-live="polite" so screen
          readers announce it without an alert's assertive interruption.
          Absent entirely when nothing disabled has been clicked. */}
      {explainerValue !== null && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            fontSize: "13px",
            lineHeight: "18px",
            color: colors.textGrey,
            padding: "4px 4px 0",
          }}
        >
          {disabledHints[explainerValue] || DISABLED_TAB_EXPLAINER}
        </Box>
      )}
    </>
  );
};

Tabs.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.string), // Array of category strings
  selectedCategory: PropTypes.string, // Currently selected category
  onCategoryChange: PropTypes.func.isRequired, // Function to handle tab change
  color: PropTypes.string, // Custom color for the tabs
  indicatorColor: PropTypes.string, // Custom indicator color
  tabStyle: PropTypes.object, // Style object for individual tabs
  disabledCategories: PropTypes.arrayOf(PropTypes.string), // Categories to disable
  disabledHints: PropTypes.objectOf(PropTypes.string), // Per-category explainer text, keyed by category/value; falls back to DISABLED_TAB_EXPLAINER
  categoriesWithIcons: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired, // Tab label
      value: PropTypes.string.isRequired, // Tab value
      icon: PropTypes.element, // Icon for the tab
    })
  ), // Array of category objects with icons
  scrollButtons: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]), // MUI Tabs scrollButtons ("auto" | true | false)
  allowScrollButtonsMobile: PropTypes.bool, // Show scroll buttons on mobile too
  ariaLabel: PropTypes.string, // Accessible name for the tab strip (screen-reader label)
  testId: PropTypes.string, // data-testid on the rendered MUI Tabs root, for QA/test hooks
};

Tabs.defaultProps = {
  categories: [],
  selectedCategory: "",
  color: "#81459B",
  indicatorColor: "#C283DA",
  tabStyle: null,
  disabledCategories: [],
  disabledHints: {},
  categoriesWithIcons: [],
};

export default Tabs;
